import { remaining } from '@quickreport/workspace';
import { useEffect, useState } from 'react';
import { useAuth, useSyncStatus } from '../auth/AuthProvider';
import { installUpdate, startFresh, usePwa } from '../lib/pwa';
import { useStorageHealth } from '../lib/storage';
import { useWorkspace } from '../lib/workspace';

const LOW_NUMBERS = 30;

function Bar({
  tone = 'warn',
  children,
  className = '',
}: {
  tone?: 'warn' | 'info' | 'error';
  children: React.ReactNode;
  className?: string;
}) {
  const colors = {
    warn: 'border-amber-200 bg-amber-50 text-warn',
    info: 'border-accent/25 bg-accent-soft text-ink',
    error: 'border-red-200 bg-red-50 text-flag',
  }[tone];
  return (
    <div
      className={`border-b px-4 py-1.5 text-[12px] md:px-6 md:py-2 md:text-[13px] ${colors} ${className}`}
      role="status"
    >
      {children}
    </div>
  );
}

/** Lab numbers are handed out in runs so reports can be numbered offline; say when a run is nearly used up. */
function NumbersBanner() {
  const ws = useWorkspace();
  const st = useSyncStatus(ws.engine);
  const [left, setLeft] = useState<number | null>(null);
  const siteId = ws.siteId;

  // biome-ignore lint/correctness/useExhaustiveDependencies: a finished sync is the cue to count again
  useEffect(() => {
    if (!siteId) return;
    let alive = true;
    remaining(ws.db, ws.orgId, siteId)
      .then((n) => alive && setLeft(n))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [ws.db, ws.orgId, siteId, st.lastSyncedAt, st.pending]);

  if (left === null || left >= LOW_NUMBERS) return null;
  return (
    <Bar tone={left === 0 ? 'error' : 'warn'}>
      {left === 0
        ? 'This computer has run out of lab numbers, so new reports cannot be made. Connect to the internet to get more.'
        : `Only ${left} lab number${left === 1 ? '' : 's'} left on this computer. Connect to the internet soon to get more.`}
    </Bar>
  );
}

/** Update prompt, offline-ready note and storage warnings, in one place. */
export function StatusBanners() {
  const { phase, reauthenticate, pinSet } = useAuth();
  const expired = phase.kind === 'ready' && phase.expired;
  const [nudged, setNudged] = useState(() => {
    try {
      return localStorage.getItem('qr.pin.nudge') === '1';
    } catch {
      return false;
    }
  });
  const pwa = usePwa();
  const { warning } = useStorageHealth();
  return (
    <>
      {expired ? (
        <Bar tone="warn">
          Your sign-in has ended, so changes are saved here but not syncing yet.{' '}
          <button type="button" className="font-medium underline" onClick={() => reauthenticate()}>
            Sign in to sync
          </button>
        </Bar>
      ) : null}
      {!pinSet && !nudged ? (
        // A PIN is for opening a computer without internet: not worth a phone's few lines.
        <Bar tone="info" className="max-md:hidden">
          Set a PIN to open LabView without internet.{' '}
          <a href="/team#pin" className="font-medium underline">
            Set a PIN
          </a>{' '}
          <button
            type="button"
            className="ml-2 underline"
            onClick={() => {
              setNudged(true);
              try {
                localStorage.setItem('qr.pin.nudge', '1');
              } catch {
                // storage blocked: it will ask again next time
              }
            }}
          >
            Not now
          </button>
        </Bar>
      ) : null}
      {pwa.updateReady ? (
        <Bar tone="info">
          {pwa.updating ? (
            <span role="status">Updating LabView… the page reloads in a moment.</span>
          ) : pwa.updateStuck ? (
            <>
              The update did not finish. Close any other LabView tabs or windows, then{' '}
              <button
                type="button"
                className="font-medium underline"
                onClick={() => void startFresh()}
              >
                start fresh
              </button>
              . Your reports and settings are kept on this computer and are not touched.
            </>
          ) : (
            <>
              A new version of LabView is ready.{' '}
              <button
                type="button"
                className="rounded-md bg-navy px-2.5 py-0.5 font-medium text-white hover:bg-navy-hover"
                onClick={installUpdate}
              >
                Update now
              </button>{' '}
              (the page reloads; everything you typed is already saved)
            </>
          )}
        </Bar>
      ) : null}
      {warning ? <Bar>{warning}</Bar> : null}
      <NumbersBanner />
    </>
  );
}
